import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FinancialExpertise } from '../../../models/financialExpertise';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-financial-expertise-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './financial-expertise-list.component.html',
  styleUrl: './financial-expertise-list.component.scss'
})
export class FinancialExpertiseListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  financialExpertises = signal<FinancialExpertise[]>([]);

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private lookupDataService: LookupDataService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadFinancialExpertises();
  }

  loadFinancialExpertises(): void {
    this.spinnerService.show();
    this.lutService.getFinancialExpertise().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortFinancialExpertises(data);
        this.financialExpertises.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading financial expertises:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortFinancialExpertises(financialExpertises: FinancialExpertise[]): FinancialExpertise[] {
    return [...financialExpertises].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateFinancialExpertise(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(financialExpertise: FinancialExpertise): void {
  }

  onEditFinancialExpertise(financialExpertise: FinancialExpertise): void {
  }

  onDeleteFinancialExpertise(financialExpertise: FinancialExpertise): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(financialExpertise.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalFinancialExpertises = this.financialExpertises();

          // 1. Optimistically remove from local state immediately
          this.financialExpertises.update(financialExpertises => financialExpertises.filter(fe => fe.id !== financialExpertise.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteFinancialExpertise(financialExpertise.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Financial expertise deleted successfully');
              // Update cache so other components get updated data
              this.lookupDataService.updateTableData('financialExpertises', this.financialExpertises());
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting financial expertise:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.financialExpertises.set(originalFinancialExpertises);
              this.toast.error(`<strong>Failed to delete Financial Expertise:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: FinancialExpertise, newName: string} | {item: FinancialExpertise, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: FinancialExpertise;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.financialExpertises.update(financialExpertises => {
      const updated = financialExpertises.map(fe => fe.id === event.item.id ? updatedItem : fe);
      return this.sortFinancialExpertises(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateFinancialExpertise(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Financial expertise updated successfully');
        // Update cache so other components get updated data
        this.lookupDataService.updateTableData('financialExpertises', this.financialExpertises());
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.financialExpertises.update(financialExpertises => {
          const updated = financialExpertises.map(fe => fe.id === event.item.id ? response : fe);
          return this.sortFinancialExpertises(updated);
        });
      },
      error: (error) => {
        console.error('Error updating financial expertise:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.financialExpertises.update(financialExpertises =>
          financialExpertises.map(fe => fe.id === event.item.id ? originalItem : fe)
        );
        this.toast.error(`<strong>Failed to update Financial Expertise:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newFinancialExpertise: FinancialExpertise = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.financialExpertises.update(financialExpertises => this.sortFinancialExpertises([...financialExpertises, newFinancialExpertise]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createFinancialExpertise({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Financial expertise created successfully');
        // Update cache so other components get updated data
        this.lookupDataService.updateTableData('financialExpertises', this.financialExpertises());
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.financialExpertises.update(financialExpertises => {
          const updated = financialExpertises.map(fe => fe.id === tempId ? response : fe);
          return this.sortFinancialExpertises(updated);
        });
      },
      error: (error) => {
        console.error('Error creating financial expertise:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.financialExpertises.update(financialExpertises =>
          financialExpertises.filter(fe => fe.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Financial Expertise:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
